import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Clock, X, Lock, Sparkles } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { ARTICLES, CONDITION_DIRECTORY, CONDITION_DIRECTORY_COUNT, FREE_CONDITIONS, hasFullAccess } from "../data";
import { CONDITION_INFO } from "../conditionInfo";
import { useAsaAuth } from "../AuthContext";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";

const CATS = ["All", "Basics", "Nutrition", "Lifestyle", "Healthcare"];

export default function Library() {
  const navigate = useNavigate();
  const { user } = useAsaAuth();
  const fullAccess = hasFullAccess(user);
  const [cat, setCat] = useState("All");
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);
  const [activeCond, setActiveCond] = useState(null);

  const articles = ARTICLES.filter((a) => (cat === "All" || a.category === cat) && a.title.toLowerCase().includes(query.toLowerCase()));
  const isLocked = (a) => !fullAccess && a.category === "Lifestyle" && !a.free;
  const openArticle = (a) => setActive(a);
  const allConditions = CONDITION_DIRECTORY.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()));
  const conditions = fullAccess ? allConditions : allConditions.filter((c) => FREE_CONDITIONS.includes(c.name));

  return (
    <AppLayout title="Education Library">
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search articles & conditions…" className="w-full h-11 pl-10 pr-4 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#0d9488] bg-white" />
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${cat === c ? "bg-[#0d9488] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0d9488]"}`}>{c}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {articles.map((a) => (
          <button key={a.id} onClick={() => openArticle(a)} data-testid={`library-article-${a.id}`} className="text-left bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow group relative">
            <div className="h-40 overflow-hidden relative">
              <img src={a.image} alt={a.title} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${isLocked(a) ? "blur-[2px] brightness-90" : ""}`} />
              {isLocked(a) && (
                <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10.5px] font-bold text-white bg-black/55 px-2 py-1 rounded-full"><Lock size={11} /> ASAL Pro</span>
              )}
              {!fullAccess && a.free && a.category === "Lifestyle" && (
                <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10.5px] font-bold text-white bg-[#22a35a] px-2 py-1 rounded-full">Free taster</span>
              )}
            </div>
            <div className="p-4">
              <span className="text-[11px] font-semibold text-[#0d9488] bg-[#e6f4f2] px-2 py-0.5 rounded-full">{a.category}</span>
              <h3 className="font-semibold text-slate-800 mt-2 leading-snug">{a.title}</h3>
              <p className="text-[13px] text-slate-500 mt-1 line-clamp-2">{a.excerpt}</p>
              {isLocked(a) ? (
                <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#0f766e] mt-2"><Sparkles size={12} /> Unlock with ASAL Pro</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[12px] text-slate-400 mt-2"><Clock size={12} /> {a.read} read</span>
              )}
            </div>
          </button>
        ))}
      </div>

      <div className="mt-10">
        <h2 className="font-head font-semibold text-slate-800 text-lg">Condition directory</h2>
        <p className="text-slate-500 text-sm mt-1">{fullAccess ? `Explore all ${CONDITION_DIRECTORY_COUNT} autoimmune & immune-mediated conditions` : `Showing ${conditions.length} of ${CONDITION_DIRECTORY_COUNT} conditions on the free plan`}{query ? ` · ${allConditions.length} match${allConditions.length === 1 ? "" : "es"}` : ""}.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {conditions.map((c) => {
            const info = CONDITION_INFO[c.name];
            return (
              <button key={c.name} onClick={() => setActiveCond(c)} data-testid={`condition-card-${c.name}`} className="text-left bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md hover:border-[#0d9488] transition-all">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-slate-800 text-[15px]">{c.name}</p>
                  <span className="text-[11px] font-semibold text-[#0d9488] bg-[#e6f4f2] px-2 py-0.5 rounded-full shrink-0">{c.category}</span>
                </div>
                <p className="text-[12px] text-slate-400 mt-1">Prevalence {c.prevalence}</p>
                {info && <p className="text-[12px] text-[#0f766e] mt-2 font-medium">View symptoms →</p>}
              </button>
            );
          })}
        </div>
        {!fullAccess && !query && (
          <div className="rounded-2xl mt-5 p-5 bg-gradient-to-r from-[#0d9488] to-[#0f766e] text-white flex flex-col sm:flex-row sm:items-center gap-3" data-testid="library-upgrade-card">
            <Lock size={22} className="shrink-0" />
            <div className="flex-1">
              <p className="font-head font-semibold">Unlock all {CONDITION_DIRECTORY_COUNT} conditions</p>
              <p className="text-white/90 text-[13px] mt-0.5">ASAL Pro opens the complete autoimmune library, plus tracking, food & lifestyle guides and reports.</p>
            </div>
            <button onClick={() => navigate("/asa/pricing")} className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-white text-[#0f766e] font-semibold text-sm shrink-0"><Sparkles size={16} /> See ASAL Pro</button>
          </div>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto kl-scroll p-0">
          {active && (
            <div>
              <DialogTitle className="sr-only">{active.title}</DialogTitle>
              <img src={active.image} alt={active.title} className="w-full h-56 object-cover rounded-t-lg" />
              <div className="p-6">
                <span className="text-[12px] font-semibold text-[#0d9488] bg-[#e6f4f2] px-2 py-0.5 rounded-full">{active.category}</span>
                <h2 className="font-head font-bold text-slate-800 text-2xl mt-3">{active.title}</h2>
                <p className="text-[13px] text-slate-400 mt-1 flex items-center gap-1"><Clock size={13} /> {active.read} read</p>
                <p className="text-slate-600 leading-relaxed mt-4">{active.excerpt}</p>
                {isLocked(active) ? (
                  <div className="mt-4 rounded-xl border-2 border-[#0d9488] bg-[#e6f4f2] p-5 text-center" data-testid="article-teaser-lock">
                    <Lock size={22} className="text-[#0f766e] mx-auto" />
                    <p className="font-head font-semibold text-slate-800 mt-2">That's the free preview</p>
                    <p className="text-[13px] text-slate-600 mt-1 leading-relaxed">This is an ASAL Pro lifestyle guide. Unlock the full article — plus symptom tracking, all 144 conditions, food & lifestyle guides and GP reports — with ASAL Pro.</p>
                    <button onClick={() => navigate("/asa/pricing")} data-testid="article-teaser-upgrade" className="mt-3 inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] text-white font-semibold text-sm transition-colors"><Sparkles size={16} /> Unlock with ASAL Pro</button>
                  </div>
                ) : (
                  <div className="mt-3 space-y-3" data-testid="article-body">
                    {(active.body || []).map((p, i) => (
                      <p key={i} className="text-slate-600 leading-relaxed">{p}</p>
                    ))}
                  </div>
                )}
                <p className="text-[12px] text-slate-400 mt-5">General information only — not a medical diagnosis. © ASA Foundation · Copyright &amp; IP: Asal Shirazi.</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!activeCond} onOpenChange={(o) => !o && setActiveCond(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto kl-scroll p-0">
          {activeCond && (
            <div className="p-6">
              <DialogTitle className="sr-only">{activeCond.name}</DialogTitle>
              <span className="text-[12px] font-semibold text-[#0d9488] bg-[#e6f4f2] px-2 py-0.5 rounded-full">{activeCond.category}</span>
              <h2 className="font-head font-bold text-slate-800 text-2xl mt-3">{activeCond.name}</h2>
              <p className="text-[13px] text-slate-400 mt-1">Prevalence {activeCond.prevalence}</p>
              {CONDITION_INFO[activeCond.name] ? (
                <>
                  <p className="text-slate-600 leading-relaxed mt-4">{CONDITION_INFO[activeCond.name].desc}</p>
                  <h3 className="font-head font-semibold text-slate-800 mt-5 mb-2">Common symptoms</h3>
                  <ul className="space-y-1.5">
                    {CONDITION_INFO[activeCond.name].symptoms.map((s) => (
                      <li key={s} className="flex items-start gap-2 text-[14px] text-slate-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#0d9488] mt-2 shrink-0" />{s}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 bg-[#e6f4f2] rounded-lg p-3 text-[13px] text-[#0f766e]">
                    See your GP if you have several of these symptoms, especially if they persist, worsen, or you have a family history of autoimmune disease. Log them in your Symptom Diary to track patterns over time.
                  </div>
                </>
              ) : (
                <p className="text-slate-600 leading-relaxed mt-4">Detailed symptom information for this condition is being added. Speak to your GP if you have concerns, and use your Symptom Diary to track how you feel over time.</p>
              )}
              <p className="text-[12px] text-slate-400 mt-5">General information only — not a medical diagnosis. Only a qualified clinician can diagnose an autoimmune condition. © ASA Foundation · Copyright &amp; IP: Asal Shirazi.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
